import {
  getHvacResults as API,
  HvacArea,
  HvacResults,
  ProjectData,
  ProjectHvacGroup,
} from '@belimo-retrofit-portal/logic';
import { Decimal } from 'decimal.js-light';
import { createSelector } from 'reselect';
import { getBasedataFormData } from 'src/modules/basedata/selectors/getBasedataFormData';
import { getProjectLazy } from 'src/modules/common/selectors/getProjectLazy';

const isAreaEnabled = (
  groups: ReadonlyArray<ProjectHvacGroup>,
  type: HvacArea,
): boolean => groups.some(
  (group) => group.areas.some(
    (area) => area.area === type && area.sections.some(
      (s) => {
        if (s.id === '33a') {
          return s.answer.actual.option !== s.answer.future.option;
        }
        if (s.id === '13a') {
          return s.answer.actual.option !== s.answer.future.option;
        }
        return s.enabled;
      },
    ),
  ),
);

type HeatingThermal = Decimal | null;
type HeatingElectrical = Decimal | null;
type CoolingElectrical = Decimal | null;
type CoolingThermal = Decimal | null;
type VentilationElectrical = Decimal | null;

type Mults = [HeatingThermal, CoolingThermal, HeatingElectrical, CoolingElectrical, VentilationElectrical];

const getMultiplicator = (
  coolingEnabled: boolean,
  heatingEnabled: boolean,
  ventilationEnabled: boolean,
): Mults => {
  switch (true) {
    case heatingEnabled && !coolingEnabled && !ventilationEnabled: {
      return [
        new Decimal(0.94),
        null,
        new Decimal(0.06),
        null,
        null,
      ];
    }
    case heatingEnabled && coolingEnabled && !ventilationEnabled: {
      return [
        new Decimal(0.67),
        new Decimal(0.26),
        new Decimal(0.04),
        new Decimal(0.03),
        null,
      ];
    }
    case heatingEnabled && coolingEnabled && ventilationEnabled: {
      return [
        new Decimal(0.61),
        new Decimal(0.23),
        new Decimal(0.04),
        new Decimal(0.02),
        new Decimal(0.09),
      ];
    }
    case heatingEnabled && ventilationEnabled && !coolingEnabled: {
      return [
        new Decimal(0.83),
        null,
        new Decimal(0.05),
        null,
        new Decimal(0.12),
      ];
    }
    case coolingEnabled && !heatingEnabled && !ventilationEnabled: {
      return [
        null,
        new Decimal(0.91),
        null,
        new Decimal(0.09),
        null,
      ];
    }
    case coolingEnabled && ventilationEnabled && !heatingEnabled: {
      return [
        null,
        new Decimal(0.67),
        null,
        new Decimal(0.07),
        new Decimal(0.26),
      ];
    }
    case ventilationEnabled && !heatingEnabled && !coolingEnabled: {
      return [
        null,
        null,
        null,
        null,
        new Decimal(1),
      ];
    }
    default:
      return [null, null, null, null, null];
  }
};

export const getHvacData = createSelector([
  getProjectLazy,
  getBasedataFormData,
], (project, form): ProjectData => {
  const coolingEnabled = isAreaEnabled(project.data.hvac.groups, HvacArea.COOLING);
  const heatingEnabled = isAreaEnabled(project.data.hvac.groups, HvacArea.HEATING);
  const ventilationEnabled = isAreaEnabled(project.data.hvac.groups, HvacArea.VENTILATION);
  const [
    heatingThermal,
    coolingThermal,
    heatingElectrical,
    coolingElectrical,
    ventilationElectrical,
  ] = getMultiplicator(
    coolingEnabled,
    heatingEnabled,
    ventilationEnabled,
  );
  return {
    ...project.data,
    hvac: {
      ...project.data.hvac,
      areas: {
        ...project.data.hvac.areas,
        [HvacArea.HEATING]: {
          ...project.data.hvac.areas[HvacArea.HEATING],
          enabled: heatingEnabled,
          thermal: {
            ...project.data.hvac.areas[HvacArea.HEATING].thermal,
            annualEnergyUse: heatingThermal ? form.annualSaving?.mul(heatingThermal) ?? null : null,
          },
          electrical: {
            ...project.data.hvac.areas[HvacArea.HEATING].electrical,
            annualEnergyUse: heatingElectrical ? form.annualSaving?.mul(heatingElectrical) ?? null : null,
          },
        },
        [HvacArea.COOLING]: {
          ...project.data.hvac.areas[HvacArea.COOLING],
          enabled: coolingEnabled,
          thermal: {
            ...project.data.hvac.areas[HvacArea.COOLING].thermal,
            annualEnergyUse: coolingThermal ? form.annualSaving?.mul(coolingThermal) ?? null : null,
          },
          electrical: {
            ...project.data.hvac.areas[HvacArea.COOLING].electrical,
            annualEnergyUse: coolingElectrical ? form.annualSaving?.mul(coolingElectrical) ?? null : null,
          },
        },
        [HvacArea.VENTILATION]: {
          ...project.data.hvac.areas[HvacArea.VENTILATION],
          enabled: ventilationEnabled,
          electrical: {
            ...project.data.hvac.areas[HvacArea.VENTILATION].electrical,
            annualEnergyUse: ventilationElectrical ? form.annualSaving?.mul(ventilationElectrical) ?? null : null,
          },
        },
      },
    },
  };
});

export const getHvacResults = createSelector([
  getHvacData,
], (data): HvacResults => API(data));
